import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orgSettingsApi, type LetterheadSlot } from '../features/orgSettings/api.ts';
import { dv } from '../i18n/dv.ts';

function ImageCard({
  slot, label, hint, hasImage,
}: { slot: LetterheadSlot; label: string; hint: string; hasImage: boolean }): JSX.Element {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    if (hasImage) {
      void orgSettingsApi.fetchImageBlobUrl(slot).then((u) => {
        if (cancelled) { URL.revokeObjectURL(u); return; }
        url = u;
        setPreviewUrl(u);
      }).catch(() => undefined);
    } else {
      setPreviewUrl(null);
    }
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [slot, hasImage]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['org'] });

  const upload = useMutation({
    mutationFn: (file: File) => orgSettingsApi.uploadImage(slot, file),
    onSuccess: () => { void invalidate(); },
  });
  const remove = useMutation({
    mutationFn: () => orgSettingsApi.removeImage(slot),
    onSuccess: () => { void invalidate(); },
  });

  return (
    <section className="card-paper space-y-4 p-5 sm:p-6">
      <h2 className="status-label text-muted-foreground">{label}</h2>
      {hasImage && previewUrl && (
        <img src={previewUrl} alt={label} className="max-h-32 rounded-xl border border-border bg-white object-contain" />
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="block w-full min-w-0 text-sm file:me-3 file:rounded-full file:border-0 file:bg-[hsl(var(--secondary-container))] file:px-4 file:py-2 file:text-[hsl(var(--on-secondary-container))]"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
          e.target.value = '';
        }}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={upload.isPending}
          onClick={() => inputRef.current?.click()}
          className="btn-primary btn-sm"
        >
          {dv.settings.uploadImage}
        </button>
        {hasImage && (
          <button
            type="button"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
            className="btn-secondary btn-sm"
          >
            {dv.settings.removeImage}
          </button>
        )}
        {upload.isSuccess && <span className="text-sm text-[hsl(var(--success))]">{dv.settings.saved}</span>}
        {upload.isError && <span className="text-sm text-destructive">{dv.common.error}</span>}
      </div>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </section>
  );
}

function FooterTextCard({ initial }: { initial: string | null }): JSX.Element {
  const qc = useQueryClient();
  const [text, setText] = useState(initial ?? '');
  // Re-sync when the loaded org changes (once), so a fresh GET populates the box.
  const syncedRef = useRef(false);
  useEffect(() => {
    if (!syncedRef.current) { setText(initial ?? ''); syncedRef.current = true; }
  }, [initial]);

  const save = useMutation({
    mutationFn: (footerText: string) => orgSettingsApi.patch({ footerText }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['org'] }); },
  });

  return (
    <section className="card-paper space-y-4 p-5 sm:p-6">
      <h2 className="status-label text-muted-foreground">{dv.settings.footerText}</h2>
      <form
        className="space-y-3"
        onSubmit={(e) => { e.preventDefault(); save.mutate(text.trim()); }}
      >
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          maxLength={500}
          className="input w-full"
        />
        <div className="flex items-center gap-2">
          <button type="submit" disabled={save.isPending} className="btn-primary btn-sm">
            {dv.common.save}
          </button>
          {save.isSuccess && <span className="text-sm text-[hsl(var(--success))]">{dv.settings.saved}</span>}
          {save.isError && <span className="text-sm text-destructive">{dv.common.error}</span>}
        </div>
      </form>
    </section>
  );
}

export function OrgSettings(): JSX.Element {
  const { data: org, isLoading } = useQuery({
    queryKey: ['org'],
    queryFn: () => orgSettingsApi.get(),
  });

  if (isLoading || !org) {
    return <p className="text-sm text-muted-foreground">{dv.common.loading}</p>;
  }

  return (
    <div className="space-y-6">
      {/* Org name is managed in Wapplify Office; shown here read-only. */}
      <section className="space-y-2">
        <h2 className="status-label text-muted-foreground">{dv.orgs.name}</h2>
        <p className="text-lg font-medium text-ink">{org.name}</p>
      </section>

      <p className="text-sm text-muted-foreground">{dv.settings.letterheadHint}</p>

      <div className="grid gap-6 lg:grid-cols-2">
        <ImageCard slot="header" label={dv.settings.headerImage} hint={dv.settings.headerSizeHint} hasImage={org.letterhead.hasHeaderImage} />
        <ImageCard slot="footer" label={dv.settings.footerImage} hint={dv.settings.footerSizeHint} hasImage={org.letterhead.hasFooterImage} />
      </div>

      <FooterTextCard initial={org.letterhead.footerText} />
    </div>
  );
}
